import React from 'react';

import {TagSchema} from 'constants/RegistryConstants';
import {REQUIRED_FIELD_ERROR_LABEL} from 'metadata/SchemaFieldValueError';
import {fieldlessValidationErrorTexts} from 'schemas/getValidationErrorText';
import {isFailed, isOverridden, isValidating} from 'schemas/SchemaValidationChecks';
import {
  SchemaValidationErrorRecord,
  SchemaValidationLongDisplayTextByStatus,
  SchemaValidationState,
  ValidationExtras,
} from 'schemas/SchemaValidationConstants';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import InlineBannerStack from 'ui-library/banners/InlineBannerStack';

interface RegisteredErrorBannersProps {
  schemaValidationState?: SchemaValidationState | null;
  validationExtras?: ValidationExtras | null;
}

interface UnregisteredErrorBannerProps {
  itemIsUnregisteredEntity: boolean;
  itemIsEntry: boolean;
  tagErrors: Array<SchemaValidationErrorRecord>;
  tagSchema?: TagSchema | null;
}

interface MetadataErrorsProps {
  registryId?: string | null;
}

/**
 * If the bioentity is invalid, return a generic error banner, plus one banner for each error not tied to
 * a schema field
 */
function RegisteredErrorBanners(props: RegisteredErrorBannersProps): JSX.Element | null {
  let banner = null;
  if (isFailed(props.schemaValidationState)) {
    const fieldlessErrorTexts = fieldlessValidationErrorTexts(props.validationExtras);
    banner = (
      <InlineBannerStack
        messages={[
          {
            bannerType: InlineBannerType.ERROR,
            text: `${SchemaValidationLongDisplayTextByStatus.FAILED}: Please fix the errors below.`,
          },
          ...fieldlessErrorTexts.map((text) => ({
            bannerType: InlineBannerType.ERROR,
            text,
          })),
        ]}
      />
    );
  } else if (isOverridden(props.schemaValidationState)) {
    banner = (
      <InlineBanner
        bannerType={InlineBannerType.WARNING}
        text={SchemaValidationLongDisplayTextByStatus.OVERRIDDEN}
      />
    );
  } else if (isValidating(props.schemaValidationState)) {
    banner = (
      <InlineBanner
        bannerType={InlineBannerType.INFO}
        text={SchemaValidationLongDisplayTextByStatus.VALIDATING}
      />
    );
  }
  return banner != null ? <div className="u-moderate-spacing-bottom">{banner}</div> : null;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any

function hasTagErrors(tagErrors?: Array<SchemaValidationErrorRecord>): boolean {
  return tagErrors != null && tagErrors.length > 0;
}

function getErrorText(tagErrors: Array<SchemaValidationErrorRecord>): string {
  const missingRequiredFields = tagErrors.some((error) => error.errorLabel === REQUIRED_FIELD_ERROR_LABEL);
  return missingRequiredFields ? 'is missing required fields' : 'contains metadata errors';
}

const getEntityMessage = (
  tagErrors?: Array<SchemaValidationErrorRecord>,
  tagSchema?: TagSchema | null
): string => {
  if (tagSchema == null) {
    return 'Please set schema to register the entity.';
  } else if (hasTagErrors(tagErrors)) {
    return `This entity ${getErrorText(tagErrors || [])}. Please fix the errors to register the entity.`;
  } else {
    // No errors
    return '';
  }
};

const getEntryMessage = (
  tagErrors?: Array<SchemaValidationErrorRecord>,
  tagSchema?: TagSchema | null
): string => {
  if (tagSchema == null) {
    return 'Please set schema.';
  } else if (hasTagErrors(tagErrors)) {
    const errorText = getErrorText(tagErrors || []);
    return `This entry ${errorText}.`;
  } else {
    // No errors
    return '';
  }
};

function UnregisteredErrorBanner(props: UnregisteredErrorBannerProps): JSX.Element | null {
  let message;
  if (props.itemIsUnregisteredEntity) {
    message = getEntityMessage(props.tagErrors, props.tagSchema);
  } else if (props.itemIsEntry) {
    message = getEntryMessage(props.tagErrors, props.tagSchema);
  } else {
    return null;
  }

  if (!message) {
    return null;
  }

  return (
    <div className="u-moderate-spacing-bottom">
      <InlineBanner bannerType={InlineBannerType.ERROR} text={message} />
    </div>
  );
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any

const MetadataErrors: React.FC<
  RegisteredErrorBannersProps & UnregisteredErrorBannerProps & MetadataErrorsProps
> = (props) => (
  <div>
    {props.registryId ? (
      <RegisteredErrorBanners
        schemaValidationState={props.schemaValidationState}
        validationExtras={props.validationExtras}
      />
    ) : (
      <UnregisteredErrorBanner
        itemIsUnregisteredEntity={props.itemIsUnregisteredEntity}
        itemIsEntry={props.itemIsEntry}
        tagSchema={props.tagSchema}
        tagErrors={props.tagErrors}
      />
    )}
  </div>
);

export default MetadataErrors;
